// file: apps/web-pharmacy/src/components/alertes/focusRecapture.js
/**
 * PHARMAKON — LA REPRISE DU FOCUS de la surcouche bloquante.
 *
 * PLAN_mobile_pharma_owner_manager §3.5, ligne « URGENT » : « focus piégé ».
 *
 * POURQUOI CE FICHIER EXISTE — le complément, pas le remplacement.
 *
 * `hooks/useFocusTrap.js` est le piège à focus MAISON, réutilisé tel quel
 * (L-5) : il fait entrer le focus au montage, cycle sur `Tab`, et le rend à
 * l'ouvrant au démontage. Son écouteur `Tab` est posé SUR LE CONTENEUR du
 * dialogue, ce qui suffit à une fenêtre modale ordinaire.
 *
 * Ça ne suffit PAS à ce palier-ci. Un clic à la SOURIS sur le voile pose le
 * focus sur `body`, hors du conteneur : l'écouteur ne voit alors plus les `Tab`
 * suivants, et le caissier peut tabuler dans le POINT DE VENTE resté derrière.
 * Une surcouche qui « bloque le comptoir » mais qu'on contourne au clavier ne
 * bloque rien — et c'est précisément ce palier qui arrête la caisse.
 *
 * On ajoute donc une reprise au NIVEAU DU DOCUMENT, mais seulement pour ce
 * composant. Le crochet partagé n'est pas touché : toutes les autres fenêtres
 * modales gardent exactement le comportement qu'elles ont aujourd'hui.
 *
 * LE PIÈGE DU PIÈGE : on tranche au TOUR SUIVANT, jamais dans l'événement.
 * Au moment où `focusout` part, `document.activeElement` vaut encore `body`
 * MÊME quand le focus est en train d'aller vers un autre élément DU dialogue.
 * Décider tout de suite ramènerait le focus sur le titre à chaque `Tab`
 * interne, et rendrait le bouton d'accusé inatteignable au clavier — on aurait
 * remplacé une fuite par une prison.
 *
 * Sans React et sans DOM réel : `doc` est injecté, donc ce module se teste en
 * environnement node comme `criticalSection.js`.
 */

/** Voir la note d'appel ci-dessous : ce sont des enveloppes, jamais les natives nues. */
const MINUTEURS_PAR_DEFAUT = Object.freeze({
  set: (fn, ms) => setTimeout(fn, ms),
  clear: (id) => clearTimeout(id),
});

/**
 * Installe la reprise. Rend la fonction de désinstallation.
 *
 * @param {() => (HTMLElement|null)} boite  donne le conteneur du dialogue
 * @param {object} [doc]                    le document (injecté pour les tests)
 * @param {object} [minuteurs]              `setTimeout` / `clearTimeout` injectables
 * @returns {() => void}
 */
export function installerRepriseFocus(
  boite,
  doc = typeof document !== "undefined" ? document : null,
  // Des ENVELOPPES, pas `{ set: setTimeout }`. Passer la fonction native nue
  // la fait appeler avec `this === minuteurs`, et `WindowOrWorkerGlobalScope`
  // exige `this === window` : le navigateur lève « Illegal invocation », la
  // surcouche bloquante plante au montage, et l'alerte URGENTE ne s'affiche
  // JAMAIS. En node le même code passe sans broncher — d'où le smoke vivant.
  minuteurs = MINUTEURS_PAR_DEFAUT,
) {
  if (!doc?.addEventListener) return () => {};

  let minuteur = null;

  const surSortie = () => {
    minuteurs.clear(minuteur);
    minuteur = minuteurs.set(() => {
      const b = boite();
      // Démonté entre-temps : il n'y a plus rien à reprendre.
      if (!b) return;
      // Le focus est déjà revenu tout seul (tabulation interne) : ne rien faire.
      if (b.contains?.(doc.activeElement)) return;
      const cible = cibleDeReprise(b);
      if (typeof cible?.focus === "function") cible.focus();
    }, 0);
  };

  doc.addEventListener("focusout", surSortie);
  return () => {
    minuteurs.clear(minuteur);
    doc.removeEventListener("focusout", surSortie);
  };
}

/**
 * Où ramener le focus.
 *
 * Le TITRE d'abord : il porte `tabIndex={-1}`, donc il est focalisable par
 * programme sans entrer dans le parcours de tabulation, et un lecteur d'écran
 * ré-annonce l'objet de l'alerte plutôt que « bouton ». Le bouton d'accusé est
 * le repli, et le conteneur le dernier recours.
 */
export function cibleDeReprise(boite) {
  return (
    boite?.querySelector?.("h2") ??
    boite?.querySelector?.("button") ??
    boite ??
    null
  );
}

export default installerRepriseFocus;
